import {
    ANNOTATION_BASE_PROPERTY,
    ANNOTATION_BASE_PROPERTY_VALUE,
    GEOM_PROPERTY,
    IS_CIRCLE_PROPERTY,
    RADIUS_PROPERTY
} from './AnnotationConstants';
import AnnotationMixin from './AnnotationMixin';
import GeoJSON from 'ol/format/GeoJSON';
import Circle from 'ol/geom/Circle';
import { isObject, isString } from '@config/utils';

export default {
    mixins: [
        AnnotationMixin,
    ],
    computed: {
        defaultAnnotationsObject() {
            let obj = {features: []};
            obj[ANNOTATION_BASE_PROPERTY] = ANNOTATION_BASE_PROPERTY_VALUE;
            return obj;
        },
    },
    methods: {
        renderRecords() {
            if(!this.source){
                this.setMapLayer();
            }
            let features = this.features();
            features.clear();
            this.recordList.forEach(rec => {
                let visibility = rec.properties.visibility;
                if(visibility){
                    let recHighlight = !!rec.style.highlight;
                    rec.features.map(jsonFeat => {
                        if(!isObject(jsonFeat)) return;
                        let fid = jsonFeat.properties.id || null;
                        let featHighlight = !!(jsonFeat.style && jsonFeat.style.highlight);
                        let feat = new GeoJSON().readFeature(jsonFeat);
                        if(feat) {
                            feat.setId(fid);
                            feat.set(GEOM_PROPERTY, feat.getGeometry().getType().toLowerCase());
                            if (feat.get(IS_CIRCLE_PROPERTY) && feat.get(RADIUS_PROPERTY) && feat.getGeometry().getType().toLowerCase() === 'point') {
                                feat.setGeometry(new Circle(feat.getGeometry().getCoordinates(), feat.get(RADIUS_PROPERTY)));
                            }
                            feat.setStyle(recHighlight || featHighlight ? this.highlightStyleFeature() :  this.styleFeature());
                            if(!features.getArray().find(f => f.getId()===fid)){
                                features.push(feat);
                            }
                        }
                    })
                }
            });
        },
        setAnnotations(annotations, replace=true) {
            if(replace) {
                this.annotations = Object.assign({}, annotations);
            }else{
                if(this.records.length>0) {
                    annotations.features.forEach(feat => {
                        if(!isObject(feat)) feat = {};
                        if(!feat.properties) feat.properties = {};
                        if(!feat.properties.id) feat.properties.id = this.generateId();
                        if(feat.properties && feat.properties.id && !this.records.find(r => r.properties.id === feat.properties.id)){
                            this.records.push(feat);
                        }
                    });
                }else{
                    this.annotations = Object.assign({}, annotations);
                }
            }
            const _map = this.map;
            if(_map) {
                this.annotations.features.forEach(feat => {
                    if(!feat.crs){
                        feat.crs = this.createCRSObject(_map.getProjection());
                    }
                });
            }

            this.renderRecords();
        },
        exportAnnotations(annotations, id=undefined, external=false) {
            let data = annotations;
            if(id) {
                data = JSON.parse(JSON.stringify(annotations));
                data.features = data.features.filter(f => f.properties.id === id);
                if(external && data[ANNOTATION_BASE_PROPERTY] === ANNOTATION_BASE_PROPERTY_VALUE) {
                    if(data.features && data.features.length>0){
                        data = data.features[0];
                    }
                }
            }
            // Convert object to Blob
            const blobConfig = new Blob(
                [ JSON.stringify(data) ],
                { type: 'text/json;charset=utf-8' }
            );
            const filename = "Annotations.json";

            //Check the Browser.
            let isIE = false || !!document.documentMode;
            if (isIE) {
                window.navigator.msSaveBlob(blobConfig, filename);
            } else {
                let url = window.URL || window.webkitURL;
                // Convert Blob to URL
                const blobUrl = url.createObjectURL(blobConfig);
                // Create an a element with blobl URL
                const anchor = document.createElement('a');
                anchor.href = blobUrl;
                anchor.target = "_blank";
                anchor.download = filename;
                // Auto click on a element, trigger the file download
                anchor.click();
                // Don't forget
                URL.revokeObjectURL(blobUrl);
            }
        },
        async uploadAnnotations(url, records) {
            let data = records;
            if(url && data.length>0) {
                let formData = {
                    data: JSON.stringify(data)
                }
                return await this.$http.post(url, formData)
                .then( res => {
                    this.showMessage(res.data.data);
                    return true;
                })
                .catch( error => {
                    this.showHttpError(error);
                    return false;
                })
            }
        },
        async downloadAnnotations(url, records) {
            let data = records;
            if(url) {
                return await this.$http.get(url)
                .then( res => {
                    let data = res.data;
                    if(data && data.data) {
                        try {
                            let features = isString(data.data) ? JSON.parse(data.data) : [];
                            if(features.length>0) {
                                this.setAnnotations({features}, false);
                            }
                            return features;
                        } catch (error) {
                            this.showError(error);
                        }
                    }
                })
                .catch( error => {
                    this.showHttpError(error);
                    return false;
                });
            }
        },
        onImportedFile(data) {
            let {file} = data;
            try {
                let replace = !!this.importFile?.replace;
                this.processFile(file)
                .then((fileContents) => {
                    if(fileContents) {
                        const annotations = JSON.parse(fileContents);
                        this.setAnnotations(annotations, replace);
                    }
                })
            } catch (error) {
                this.showError(error);
            }
        },
        fileValidation(file) {
            return (async () => await this.checkValidFile(file))();
        },
        async checkValidFile(file) {
            try {
                return await this.processFile(file).then((fileContents) => {
                    if(fileContents) {
                        const json = JSON.parse(fileContents);
                        return json[ANNOTATION_BASE_PROPERTY] === ANNOTATION_BASE_PROPERTY_VALUE && json.features && json.features.length>0;
                    }
                    return false;
                })
            } catch (error) {
                console.error(error);
            }
            return false;
        },
        async processFile(file) {
            try {
                const fileContents = await this.readFileAsText(file);
                return fileContents;
            } catch(error) {
                console.error(error);
            }
            return null;
        },
        readFileAsText(inputFile) {
            const reader  = new FileReader();

            return new Promise((resolve, reject) => {
                reader.onerror = () => {
                    reader.abort();
                    reject(new DOMException("Problem parsing input file."));
                };
                reader.onload = (fileEvent) => {
                    const fileContents = fileEvent.target.result;
                    resolve(fileContents);
                };

                reader.readAsText(inputFile);
            })
        },
    },
}